import { useRequest } from 'features/request/useRequest';
import type { DismantlingCoefficientTypeEnum } from 'gql/graphql';
import { uerPriceListUerForDismantlingRatioGql } from 'pages/Project/UerProject/Ratio/CreateOrEditRatio/createOrEditRatioQueries';

import { useMemo } from 'react';

import type { IrowGroupTitleItem } from '../components/UerSelectionModal';

const requestId = 'uerPriceListUerForDismantlingRatios';

interface IUseUerPriceListUerForDismantlingRatios {
    currentFilters?: Record<string, string[]> | null;
    filterValues: Record<string, string[]>;
    dataVariables: {
        contractId: number;
        groupType: DismantlingCoefficientTypeEnum | null;
        preselectedUerBaseRowIds: number[] | null[];
    };
    groupStates: Record<string, boolean>;
    isRatiosGridShow: boolean;
}

const getWhereVariables = (filterValues?: Record<string, string[]>) => {
    if (!filterValues) {
        return undefined;
    }

    return {
        and: Object.entries(filterValues).reduce((acc: any, [key, value]) => {
            if (!value.length) {
                return acc;
            }

            if (key === 'groupName') {
                return [
                    ...acc,
                    {
                        uerGroupName: { in: value },
                    },
                ];
            }

            if (value.length) {
                acc = [
                    ...acc,
                    {
                        [key]: { in: value },
                    },
                ];
            }

            return acc;
        }, []),
    };
};

export const useUerDataTableRatios = (props: IUseUerPriceListUerForDismantlingRatios) => {
    const { filterValues, dataVariables, groupStates, isRatiosGridShow } = props;

    const variables = useMemo(
        () => ({
            ...dataVariables,
            where: getWhereVariables(filterValues),
        }),
        [dataVariables, filterValues],
    );

    const [uerPriceListUerForDismantlingRatioData, , ,] = useRequest({
        requestId,
        query: uerPriceListUerForDismantlingRatioGql,
        withEffect: isRatiosGridShow,
        variables,
    });

    const groupedData = useMemo(
        () =>
            uerPriceListUerForDismantlingRatioData?.uerPriceListUerForDismantlingRatio?.items?.reduce((acc, row) => {
                const { ratioCode } = row;

                if (!ratioCode) {
                    return acc;
                }

                if (!acc[ratioCode]) {
                    acc[ratioCode] = [];
                }

                acc[ratioCode]?.push(row);

                return acc;
            }, {} as any),
        [uerPriceListUerForDismantlingRatioData?.uerPriceListUerForDismantlingRatio?.items],
    );

    const rowData = useMemo(
        () =>
            Object.entries(groupedData || {}).reduce<IrowGroupTitleItem[]>((acc, [ratioCode, rows]) => {
                if (Array.isArray(rows)) {
                    acc.push({ isGroup: true, ratioCode, rowCount: rows.length });
                    if (!groupStates[ratioCode]) {
                        acc.push(...rows);
                    }
                }

                return acc;
            }, []),
        [groupedData, groupStates],
    );

    return {
        rowData,
        itemsData: uerPriceListUerForDismantlingRatioData?.uerPriceListUerForDismantlingRatio?.items,
    };
};
